import React, { lazy } from 'react';
import { Box } from '@mui/material';
import Hero from '../components/Hero';
import LazySection from '../components/LazySection';
import SlatWipes from '../components/SlatWipes';

/* Lazy-load below-the-fold sections so the hero (and the JS needed to
   render it) ship in the initial bundle, and everything else is split
   into its own chunk fetched as the page becomes visible.
   `Suspense fallback={null}` means each section appears as soon as its
   chunk lands — no spinner flash. The `content-visibility: auto`
   wrapper keeps off-screen sections cheap (skip layout/paint until they
   approach the viewport). */
const About = lazy(() => import('../components/About'));
const OurStory = lazy(() => import('../components/OurStory'));
const Services = lazy(() => import('../components/Services'));
const ImageCarousel = lazy(() => import('../components/ImageCarousel'));
const Products = lazy(() => import('../components/Products'));
const Appointment = lazy(() => import('../components/Appointment'));
const Contact = lazy(() => import('../components/Contact'));

const snap = { scrollSnapAlign: 'start', scrollSnapStop: 'normal' };

/* Section handovers for <SlatWipes>. `from` is the outgoing section's id,
   `color` the ground the incoming section paints itself — so the blind that
   covers the old section is already wearing the new one's colour when it opens.
   Declared at module level on purpose: a fresh array each render would re-run
   the effect's scroll wiring every render. */
const WIPE_BOUNDARIES = [
  { from: 'hero', color: '#f5efe6' },            // → About (Atelier Cream)
  { from: 'about', color: '#000000' },           // → Our Story (ink)
  { from: 'our-story', color: '#ffffff' },       // → Services (white)
  { from: 'services', color: '#000000' },        // → Carousel (ink)
  { from: 'carousel-section', color: '#fafafa' },// → Products (near-white)
  { from: 'products', color: '#000000' },        // → Appointment (ink)
  { from: 'appointment', color: '#ffffff' },     // → Contact (white)
];

const cvAuto = (minHeight) => ({
  contentVisibility: 'auto',
  containIntrinsicSize: `${minHeight}px`,
});

const HomePage = ({ onBookingClick }) => {
  return (
    <Box sx={{ width: '100%' }}>
      <SlatWipes boundaries={WIPE_BOUNDARIES} />
      {/* No id here — Hero's own root Box already carries id="hero", which is
          what SlatWipes measures. */}
      <Box sx={snap}>
        <Hero onBookingClick={onBookingClick} />
      </Box>
      {/* Each section is gated on approaching the viewport — see LazySection.
          `content-visibility` alone still let their images and videos download
          on first load, which is what starved the hero of bandwidth. */}
      <LazySection id="about" minHeight={900} sx={{ ...snap, ...cvAuto(900) }}>
        <About />
      </LazySection>
      <LazySection id="our-story" minHeight={1100} sx={{ ...snap, ...cvAuto(1100) }}>
        <OurStory />
      </LazySection>
      <LazySection id="services" minHeight={900} sx={{ ...snap, ...cvAuto(900) }}>
        <Services />
      </LazySection>
      <LazySection id="carousel-section" minHeight={700} sx={{ ...snap, ...cvAuto(700) }}>
        <ImageCarousel />
      </LazySection>
      <LazySection id="products" minHeight={1100} sx={{ ...snap, ...cvAuto(1100) }}>
        <Products />
      </LazySection>
      <LazySection id="appointment" minHeight={800} sx={{ ...snap, ...cvAuto(800) }}>
        <Appointment onBookingClick={onBookingClick} />
      </LazySection>
      <LazySection id="contact" minHeight={900} sx={{ ...snap, ...cvAuto(900) }}>
        <Contact />
      </LazySection>
    </Box>
  );
};

export default HomePage;
